/**
 * Scout v0 — Layer 1 summary (workspace always-on criteria profile).
 *
 * Read-only here: the identity + always-on suppressions (clients /
 * competitors / bad-fit) are edited on the Workspace Criteria screen
 * (/scout/suppressions), not in the composer. The composer only displays them
 * so the operator sees the full composed spec context while building
 * layer 2 + 3. An unset identity hard-blocks compose (400 IDENTITY_MISSING),
 * so the empty state links straight to the editor.
 */

import { Link } from '@tanstack/react-router';
import type { CriteriaProfile } from '@/types/scout';
import { EmptyState, LoadingSpinner } from '@/components/ui';

interface Layer1SummaryProps {
  profile: CriteriaProfile | undefined;
  isLoading: boolean;
}

function SuppressionList({ label, items }: { label: string; items: string[] | undefined }) {
  const list = items ?? [];
  return (
    <div>
      <p className="text-xs font-medium text-muted mb-1">{label}</p>
      {list.length === 0 ? (
        <p className="text-sm text-muted italic">(none)</p>
      ) : (
        <ul className="flex flex-wrap gap-1.5">
          {list.map((item) => (
            <li
              key={item}
              className="badge badge-warning"
              title="Always-on suppression - edited in Workspace Criteria"
            >
              {item}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function Layer1Summary({ profile, isLoading }: Layer1SummaryProps) {
  if (isLoading) {
    return (
      <div className="card">
        <LoadingSpinner className="py-6" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="card">
        <EmptyState
          title="No workspace criteria profile"
          description="The always-on layer could not be loaded. Edited in Workspace Criteria."
        />
      </div>
    );
  }

  return (
    <section
      className="card"
      aria-labelledby="layer1-heading"
      aria-description="Layer 1 - workspace always-on criteria (read-only in the composer)"
    >
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 id="layer1-heading" className="text-lg font-semibold text-text">
            Layer 1 - always-on
          </h2>
          <p className="text-xs text-muted mt-0.5">Workspace identity + suppressions (read-only)</p>
        </div>
        <span className="badge badge-secondary">read-only</span>
      </div>

      <dl className="space-y-3">
        <div>
          <dt className="text-xs font-medium text-muted mb-1">Identity</dt>
          <dd className="text-sm text-text">
            {profile.identity || (
              <span className="text-warning">
                (not set) - composing is blocked until the workspace identity is set.{' '}
                <Link to="/scout/suppressions" className="underline">
                  Set it in Workspace Criteria
                </Link>
              </span>
            )}
          </dd>
        </div>
        <SuppressionList label="Clients (never outreach)" items={profile.suppressions.clients} />
        <SuppressionList label="Competitors" items={profile.suppressions.competitors} />
        <SuppressionList label="Bad-fit" items={profile.suppressions.badFit} />
      </dl>
    </section>
  );
}